import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Button, Chip } from '@structural-climbing/ui'
import type { GymPlan } from '../plans/types'
import { AssignPlanModal } from './AssignPlanModal'
import { SubBadge } from './SubBadge'
import { formatDate, type ProfileSubscriptionData } from './ProfileSubscription'

interface Props {
  current: ProfileSubscriptionData | null
  upcoming: ProfileSubscriptionData | null
  /** Whoever may sell plans: the button is hidden from everyone else. */
  canRenew: boolean
  memberEmail: string
  memberUserId: string
  memberName?: string
  childId?: string
  childName?: string
  onRenewed: () => void
}

/**
 * Under the current plan: the renewal queued behind it, or a button to sell one.
 * A renewal starts the day after the current plan ends, so a member who renews
 * early never loses a day and never pays twice for the same days.
 */
export function RenewalSection({
  current,
  upcoming,
  canRenew,
  memberEmail,
  memberUserId,
  memberName,
  childId,
  childName,
  onRenewed,
}: Props) {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string
  const [plans, setPlans] = useState<GymPlan[] | null>(null)
  const [opening, setOpening] = useState(false)

  if (upcoming) return <UpcomingPlanCard sub={upcoming} />

  const renewable =
    canRenew &&
    !!current?.gymPlanId &&
    !!current.expiresAt &&
    (current.status === 'active' || current.status === 'frozen')
  if (!renewable || !current) return null

  async function openRenew() {
    setOpening(true)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/gym-plans`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.ok) setPlans((await res.json()) as GymPlan[])
    } finally {
      setOpening(false)
    }
  }

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {current.renewalOffer && (
        <Chip size="sm" color="success" variant="flat">
          {t('members.renewalOfferChip', { percent: current.renewalOffer.percent })}
        </Chip>
      )}
      <Button size="sm" variant="flat" color="primary" isLoading={opening} onClick={openRenew}>
        {t('members.renewPlan')}
      </Button>
      {plans && (
        <AssignPlanModal
          memberEmail={memberEmail}
          memberUserId={memberUserId}
          memberName={memberName}
          childId={childId}
          childName={childName}
          plans={plans}
          renewOf={{
            subscriptionId: current.id,
            gymPlanId: current.gymPlanId!,
            planName: current.planName,
            expiresAt: current.expiresAt,
            offerPercent: current.renewalOffer?.percent ?? null,
          }}
          onAssigned={() => {
            setPlans(null)
            onRenewed()
          }}
          onClose={() => setPlans(null)}
        />
      )}
    </div>
  )
}

/** The renewal waiting behind the current plan. */
export function UpcomingPlanCard({ sub }: { sub: ProfileSubscriptionData }) {
  const { t } = useTranslation()
  return (
    <div>
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-foreground/40">
        {t('members.nextPlan')}
      </p>
      <div className="rounded-lg border border-dashed border-divider px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium">{sub.planName}</p>
          <SubBadge status={sub.status} />
        </div>
        {sub.startsAt && (
          <p className="mt-0.5 text-xs text-foreground/50">
            {t('members.subStart', { date: formatDate(sub.startsAt) })}
            {sub.expiresAt && ` · ${t('members.subEnd', { date: formatDate(sub.expiresAt) })}`}
          </p>
        )}
        {sub.entriesRemaining != null && (
          <p className="mt-0.5 text-xs text-foreground/50">
            {t('members.subEntriesRemaining', { count: sub.entriesRemaining })}
          </p>
        )}
        {sub.renewalDiscountPercent != null && (
          <Chip size="sm" color="success" variant="flat" className="mt-1.5">
            {t('members.renewalDiscountGiven', { percent: sub.renewalDiscountPercent })}
          </Chip>
        )}
      </div>
    </div>
  )
}
